
import React, { FC, useState, useCallback, useEffect } from 'react';
import {
    Badge,
    Menu,
    MenuButton,
    MenuTrigger,
    MenuPopover,
    MenuList,
    MenuItem,
    MenuDivider, Link,
    Tooltip,
    Button
} from '@fluentui/react-components';

import { MenuIcon, X, MessagesSquare, Download, LogOut, Sun, Moon, LaptopMinimal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUserContext } from '../../Contexts/UserContext';
import { useTheme } from '../../store/hooks';
import './HeaderViewMobile.scss';
import SyButton from '../Common/SyButton';
import MenuPersona from './MenuPersona';
import { AppThemeMode, FORCED_DARK_THEME_MODE, FORCED_LIGHT_THEME_MODE, FROM_DEVICE_THEME_MODE } from '../../store/models/theme';
import Skeleton from 'react-loading-skeleton';
import { useRootState } from '../../store/hooks';


export interface HeaderPropsMobile {
    // onMenuToggle now receives a boolean indicating the desired "open" state (true = open, false = closed)
    onMenuToggle?: (open: boolean) => void;
    isMenuOpen?: boolean;
    onNewChatClick?: () => void;
    onToggleTheme?: () => void;
    themeMode: AppThemeMode;
    handleLogout: () => void;
    handleInstallApp: () => void;
    userPhoto?: string;
}

export const HeaderViewMobile: FC<HeaderPropsMobile> = (props: HeaderPropsMobile) => {
    const { onMenuToggle, onNewChatClick, isMenuOpen, onToggleTheme, themeMode, 
        handleLogout,handleInstallApp, userPhoto
    } = props;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { userProfile } = useUserContext();
    const { theme } = useTheme();
    const [isMobileOverlayOpen, setIsMobileOverlayOpen] = useState(false);
    const [isMenuProfileOpen, setIsMenuProfileOpen] = useState(false);
    // États pour gérer le swipe (déclarés en haut pour éviter hooks conditionnels)
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);

    // Distance minimale de swipe (en pixels)
    const minSwipeDistance = 50;

    const handleNavigate = useCallback((path: string) => {
        navigate(path);
        setIsMobileOverlayOpen(false);
    }, [navigate]);

    const onTouchStart = (e: React.TouchEvent) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientY);
        console.log('Touch start:', e.targetTouches[0].clientY);
    };

    const onTouchMove = (e: React.TouchEvent) => {
        setTouchEnd(e.targetTouches[0].clientY);
        console.log('Touch move:', e.targetTouches[0].clientY);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        console.log('Swipe distance:', touchEnd - touchStart);
        const distance = touchEnd - touchStart;
        const isDownSwipe = distance > minSwipeDistance;
        
        if (isDownSwipe) {
            // Fermer le menu si swipe vers le bas
            setIsMenuProfileOpen(false);
        }
    };

    const { state } = useRootState();
    const appLoading = state.appLoading ?? false;
    const conversationsLoading = state.conversationsLoading ?? false;
    const loading = appLoading || conversationsLoading;

    if (loading) {
        return (
            <header className="header-view">
                <div className="header-view__left">
                    <div className="header-view__left__infoAppGroup">
                        <span className="header-view__left__logoAndAppName">
                            <Skeleton className="skeleton-rect-36" />
                            <Skeleton className="skeleton-brand" />
                        </span>
                    </div>
                </div>

                <div className="header-view__right">
                    <Skeleton className="skeleton-rect-36" />
                    <Skeleton className="skeleton-rect-36" />
                    <Skeleton className="skeleton-rect-36 ml-auto" />
                </div>
            </header>
        );
    }
    


    return (
        <>
            <header className="header-view-mobile">
                {/* Left Section - Logo */}

                <div className="header-view-mobile__left">
                    <SyButton appearance="tertiary" className='themeButton' iconOnly icon={isMenuOpen ? <X /> : <MenuIcon />} onClick={() => onMenuToggle && onMenuToggle(!isMenuOpen)}></SyButton>
                    <SyButton appearance="tertiary" className='themeButton' iconOnly icon={<MessagesSquare className='newChatIcon' />} onClick={() => {
                        if (isMenuOpen && onMenuToggle) {
                            onMenuToggle(false);
                        }
                        onNewChatClick?.();
                    }}></SyButton>
                </div>
                <div className="header-view-mobile__center">
                    {/* Syensqo Logo  */}
                    <img className="syensqo-logo" src="Syensqo-Logo.svg" alt="Syensqo"></img>
                </div>
                {/* Right Section - Actions and Profile */}
                <div className="header-view-mobile__right">
                    {/* Dark/Light/DeviceControl */}
                    <SyButton appearance="tertiary" className='themeButton' onClick={onToggleTheme} iconOnly
                        icon={themeMode === FORCED_LIGHT_THEME_MODE ? <Sun /> : themeMode === FORCED_DARK_THEME_MODE ? <Moon /> : <LaptopMinimal />}></SyButton>
                    {/* User Profile Menu 
                    <SyButton appearance="tertiary" className='themeButton' onClick={() => handleLogout()} iconOnly>
                        <MenuPersona
                            photo={userPhoto}
                        />
                    </SyButton>*/}
                     {/* User Profile Menu */}
                    <Menu positioning="below-end" open={isMenuProfileOpen} onOpenChange={(_e, data) => setIsMenuProfileOpen(data.open)}>
                        <MenuTrigger disableButtonEnhancement>
                            <MenuButton className="header-view__profile">
                                <MenuPersona
                                    photo={userPhoto}
                                />
                            </MenuButton>
                        </MenuTrigger>
                        <MenuPopover className="header-view-mobile__profile-popover">
                            <MenuList 
                                className='header-view__profile__menu'
                                onTouchStart={onTouchStart}
                                onTouchMove={onTouchMove}
                                onTouchEnd={onTouchEnd}
                            >
                                <MenuItem icon={<Download />} onClick={() => handleInstallApp()}>
                                    {t('header.install', 'Install SyGPT')}
                                </MenuItem>
                                <MenuDivider />
                                <MenuItem className='logOutMenuItem' icon={<LogOut />} onClick={() => handleLogout()} >
                                    {t('header.logout', 'Log out')}
                                </MenuItem>
                            </MenuList>
                        </MenuPopover>
                    </Menu>
                </div>
            </header>
        </>
    );
};
